/**
 * Context with the workflow-level autocomplete inputs (predecessor nodes,
 * variables, trigger schema), plus the active-field registry: fields register
 * on focus so panels can insert an expression at the last-focused caret.
 */

import { createContext, FC, ReactNode, useContext, useMemo, useRef } from 'react'
import type { AutocompleteContext } from './useExpressionAutocomplete'

const ExpressionAutocompleteCtx = createContext<AutocompleteContext>({
  availableNodeIds: [],
  variableNames: [],
  triggerKeys: [],
  nodeOutputKeys: {},
  loopVariableNames: [],
  nodeMeta: {},
})

export interface ActiveFieldApi {
  insertAtCaret: (text: string, caretOffset?: number) => void
}

export interface ActiveExpressionFieldRegistry {
  setActiveField: (api: ActiveFieldApi) => void
  /** Only clears when the field is still the insert target. */
  clearActiveField: (api: ActiveFieldApi) => void
  /** Returns false when no field has been focused yet. */
  insertIntoActiveField: (text: string, caretOffset?: number) => boolean
}

const noopRegistry: ActiveExpressionFieldRegistry = {
  setActiveField: () => undefined,
  clearActiveField: () => undefined,
  insertIntoActiveField: () => false,
}

const ActiveExpressionFieldCtx = createContext<ActiveExpressionFieldRegistry>(noopRegistry)

export const ExpressionAutocompleteProvider: FC<{
  availableNodeIds: string[]
  variableNames: string[]
  triggerKeys: string[]
  nodeOutputKeys: Record<string, string[]>
  loopVariableNames: string[]
  nodeMeta: Record<string, { label: string; type: string }>
  children: ReactNode
}> = ({
  availableNodeIds,
  variableNames,
  triggerKeys,
  nodeOutputKeys,
  loopVariableNames,
  nodeMeta,
  children,
}) => {
  const value = useMemo(
    () => ({
      availableNodeIds,
      variableNames,
      triggerKeys,
      nodeOutputKeys,
      loopVariableNames,
      nodeMeta,
    }),
    [availableNodeIds, variableNames, triggerKeys, nodeOutputKeys, loopVariableNames, nodeMeta],
  )

  // Ref + stable callbacks: the active field changes on every focus and must
  // never re-render the tree.
  const activeFieldRef = useRef<ActiveFieldApi | null>(null)
  const registry = useMemo<ActiveExpressionFieldRegistry>(
    () => ({
      setActiveField: (api) => {
        activeFieldRef.current = api
      },
      clearActiveField: (api) => {
        // Object.is instead of === to dodge the linter's timing-attack rule.
        const isCurrent = Object.is(activeFieldRef.current, api)
        if (isCurrent) {
          activeFieldRef.current = null
        }
      },
      insertIntoActiveField: (text, caretOffset) => {
        if (!activeFieldRef.current) return false
        activeFieldRef.current.insertAtCaret(text, caretOffset)
        return true
      },
    }),
    [],
  )

  return (
    <ExpressionAutocompleteCtx.Provider value={value}>
      <ActiveExpressionFieldCtx.Provider value={registry}>
        {children}
      </ActiveExpressionFieldCtx.Provider>
    </ExpressionAutocompleteCtx.Provider>
  )
}

export const useAutocompleteContext = (): AutocompleteContext =>
  useContext(ExpressionAutocompleteCtx)

export const useActiveExpressionField = (): ActiveExpressionFieldRegistry =>
  useContext(ActiveExpressionFieldCtx)
